import Joi from 'joi';
import { ROLLUP_COUNTRIES_WITH_GLOBAL } from 'shared/metrics/constants';
import { inputSearchTypes, defaultInputSearchTypes } from 'utils/search';
import { PLACEMENT_STATUSES } from '../constants';

const defaultStatus = PLACEMENT_STATUSES.filter(
  (status) => status !== 'deleted',
).sort();

const arrayOfIds = Joi.array().items(Joi.number()).default([]);

const schema = {
  // Quick Filters
  sort: Joi.string().default('lastEdit-desc'),
  limit: Joi.number().default(24),
  skip: Joi.number().default(0),
  query: Joi.string().allow(null, '').default(''),
  searchType: Joi.string()
    .valid(...inputSearchTypes.map((searchType) => searchType.value))
    .default(defaultInputSearchTypes.value),
  status: Joi.array()
    .items(Joi.string().valid(...PLACEMENT_STATUSES))
    .default(defaultStatus),
  placedBy: Joi.array().items(Joi.string()).default([]),
  method: Joi.array().items(Joi.string()).default([]),
  clientId: arrayOfIds,
  brandId: arrayOfIds,
  brandIdExclude: arrayOfIds,
  presetId: Joi.number().allow(null),

  // Associations
  campaignId: Joi.number(),
  productionId: arrayOfIds,
  productionIdExclude: arrayOfIds,
  studioId: arrayOfIds,
  studioIdExclude: arrayOfIds,
  productionCompanyId: arrayOfIds,
  productionCompanyIdExclude: arrayOfIds,
  categoryId: arrayOfIds,
  categoryIdExclude: arrayOfIds,
  locations: Joi.array().items(Joi.string()).default([]),
  countryOfOrigin: Joi.array().items(Joi.string()),

  // Workflow
  coordinatorId: arrayOfIds,
  coordinatorIdExclude: arrayOfIds,
  scannerId: arrayOfIds,
  scannerIdExclude: arrayOfIds,

  // Premiere
  premiere: Joi.object()
    .keys({
      date: Joi.object()
        .keys({
          from: Joi.string().allow(null),
          to: Joi.string().allow(null),
        })
        .allow(null),
      country: Joi.string()
        .valid(...ROLLUP_COUNTRIES_WITH_GLOBAL)
        .allow(null),
      isGlobal: Joi.boolean().allow(null),
    })
    .default({}),

  // Premiere
  metric: Joi.object()
    .keys({
      type: Joi.array().items(Joi.string()).allow(null),
      window: Joi.string().allow(null),
      country: Joi.string()
        .valid(...ROLLUP_COUNTRIES_WITH_GLOBAL)
        .allow(null),
    })
    .default({}),

  // Attributes
  attr: Joi.object().keys({
    addedValue: Joi.boolean(),
    hasImage: Joi.boolean(),
    hasVideo: Joi.boolean(),
    reelReady: Joi.boolean(),
  }),
  showOnly: Joi.object().keys({
    highConnectivity: Joi.boolean(),
  }),
  storyType: Joi.string().allow(''),
  source: Joi.array().items(Joi.string().allow('')),
  verbals: Joi.string().allow(''),
  quality: Joi.array().items(Joi.string()).default([]),
  episode: Joi.string().allow(''),
  season: Joi.string().allow(''),
  description: Joi.string().allow(''),
  friendlyId: Joi.array().items(Joi.number()).default([]),
  friendlyIdExclude: Joi.array().items(Joi.number()).default([]),
  productionType: Joi.array().items(Joi.string()).default([]),
  hasProblem: Joi.string().allow(''),
  aiDetectedProblem: Joi.string()
    .valid('all', 'none', 'detected', 'reviewed')
    .default('all'),
  trackerUrl: Joi.string().allow(null, ''),
  hasTrackerUrl: Joi.string().valid('yes', 'no', 'all').default('all'),

  // used by form navigation
  deliverableId: arrayOfIds,
  sourceId: Joi.number(),
  channels: Joi.array()
    .items(
      Joi.object({
        channel: Joi.string(),
        classes: Joi.array()
          .items(
            Joi.object({
              class: Joi.string(),
              genres: Joi.array()
                .items(
                  Joi.object({
                    genre: Joi.string(),
                    subgenres: Joi.array().items(Joi.string()).allow(null),
                  }),
                )
                .allow(null),
            }),
          )
          .allow(null),
      }),
    )
    .allow(null)
    .default([]),
  productionConnectedToWatch: Joi.string()
    .valid('yes', 'no', 'all')
    .default('all'),
  connectedToWatch: Joi.string().valid('yes', 'no', 'all').default('all'),
  connectedToSpotlight: Joi.string().valid('yes', 'no', 'all').default('all'),
  spotlightTags: Joi.array().items(Joi.string()).default([]),
  createdBySpotlight: Joi.boolean().default(false),
  updatedBySpotlight: Joi.boolean().default(false),
  notCreatedBySpotlight: Joi.boolean().default(false),
  affectedByMerge: Joi.boolean().default(false),
  showCompetitionBrands: Joi.boolean().default(false),

  // Card/List view mode, this will be excluded from the GraphQL query.
  listMode: Joi.string().default('row'),
};

export default schema;

export const getDefaultValue = () => schema;
